import { FC, useEffect, useState } from "react";
import AppLayout from "../../components/layout/app-layout/app-layout";
import Field from "../../components/shared/Field/Field";
import Card from "../../components/shared/Card/card";
import styles from "./page-supplier-presentation.module.scss";
import BtButton from "../bt-button/bt-button";
import ServiceSales, { Contact, OtherEmailAdress } from "../../services/service-sales";
import Selector, { SelectorType } from "../../components/shared/Selector/Selector";
import { MultiValue } from "react-select";
import { NotificationManager } from "react-notifications";
import { Loading } from "../../components/shared";
import LogoForm from "../app-add-supplier/logoForm";
import { FileScope } from "../../util/appEnum";
import PlaceSelector from "./PlaceSelector";

import { ReactComponent as ForwardIcon } from '../../assets/svg/forward.svg';
import { ReactComponent as AddUserIcon } from '../../assets/svg/add-user.svg';
import { ReactComponent as RemoveUserIcon } from '../../assets/svg/remove-user.svg';
import { ReactComponent as CommentIcon } from '../../assets/svg/comment.svg';
import { ReactComponent as SoderbergpartnersLogo } from '../../assets/svg/soderbergpartners.svg';
import { ReactComponent as AccuratorLogo } from '../../assets/images/aceve/accurator_dark.svg';
import { ReactComponent as ShoppingCartIcon } from '../../assets/svg/shopping_cart.svg';
import Modal from "../../components/shared/Modal/Modal";
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import { logInsuranceClick } from "../../services/service-logs";
import appState from "../../state/AppStateContainer";
import FileUploadRow, { ExistingFile, NewFile } from "../../components/shared/FileUploadRow/FileUploadRow";
import FileUpload from "../../components/shared/FileUpload/FileUpload";
import { useTranslation } from "react-i18next";
import DatePicker from "../../components/shared/DatePicker/DatePicker";

type Props = {

};

const newId = () => "new"+Date.now();

enum FieldName {
  name = "name",
  email = "email",
  phone = "phone",
}

const PageSupplierPresentation : FC<Props> = () => {
  const [t] = useTranslation('supplierPresentation');
  const [name, setName] = useState("");
  const [orgNr, setOrgNr] = useState("");
  const [logo, setLogo] = useState<{ file: string, name: string }>();
  const [newLogo, setNewLogo] = useState<File>();
  const [presentation, setPresentation] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [areas, setAreas] = useState<SelectorType[]>([]);
  const [places, setPlaces] = useState<string[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [otherEmailAddresses, setOtherEmailAddresses] = useState<OtherEmailAdress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectEmail, setRedirectEmail] = useState("");
  const [redirectMessage, setRedirectMessage] = useState("");
  const [redirectValue, setRedirectValue] = useState<SelectorType>(null);

  const [showMessage, setShowMessage] = useState(false);
  const [messageEmail, setMessageEmail] = useState("");
  const [messageValue, setMessageValue] = useState("");

  const [areaOptions, setAreaOptions] = useState<SelectorType[]>([]);

  const [existingLiabilityInsurances, setExistingLiabilityInsurances] = useState<{ _id?: string, file?: { fileName: string, file: string, mimeType: string }, comment: string, validUntilDate?: Date }[]>([]);
  const [newLiabilityInsurances, setNewLiabilityInsurances] = useState<NewFile[]>([]);
  const [focusedDatePicker, setFocusedDatePicker] = useState<{ sectionKey: string; index: number; isNew: boolean } | null>(null);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [errorMessages, setErrorMessages] = useState<{[key: string]: {[x in FieldName]: string}}>({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [supplierProfile, areaOptions] = await ServiceSales.fetchSupplierProfile();
      setName(supplierProfile.name);
      if (supplierProfile.orgNr) setOrgNr(supplierProfile.orgNr);
      if (supplierProfile.logo) setLogo(supplierProfile.logo);
      setPresentation(supplierProfile.presentation);
      setAddress(supplierProfile.address);
      setWebsite(supplierProfile.website);
      setAreas(supplierProfile.areas.map(area => ({ label: area.name, value: area._id })));
      setPlaces(supplierProfile.places);
      setContacts(supplierProfile.contacts);
      setOtherEmailAddresses(supplierProfile.otherEmailAddresses);
      setAreaOptions(areaOptions.map(a => ({label: a.name, value: a._id})));
      setExistingLiabilityInsurances((supplierProfile as any).liabilityInsurances || []);
      setExistingFiles((supplierProfile as any).existingFiles || []);
      setLoading(false);
    })();
  }, []);

  const onSave = async () => {
    setSaving(true);
    const convertedAreas = areas.map(a => ({_id: a?.value ?? "", name: a?.label ?? ""}));
    try {
      const response = await ServiceSales.saveSupplierProfile({
        name, logo, newLogo, presentation, address, website, areas: convertedAreas, places, contacts, otherEmailAddresses,
        existingLiabilityInsurances, newLiabilityInsurances, existingFiles: existingFiles as any, newFiles: newFiles as any
      } as any);
      if (response.errorMessages) {
        setErrorMessages(response.errorMessages);
        setSaving(false);
        return;
      }
      setLogo(response.logo);
      setNewLogo(undefined);
      setExistingLiabilityInsurances(response.liabilityInsurances);
      setNewLiabilityInsurances([]);
      setContacts(response.contacts);
      setOtherEmailAddresses(response.otherEmailAddresses);
      setExistingFiles(response.existingFiles);
      setNewFiles([]);
      setErrorMessages({});
      const supplierProfileHasBeenUpdated = await ServiceSales.hasProfileBeenUpdated();
      appState.setSupplierProfileHasBeenUpdated(supplierProfileHasBeenUpdated);
      NotificationManager.success(t('notification.success', 'Sparat'));
    } catch (e) {
      NotificationManager.error((e as Error).toString(), t('notification.error', 'Fel'));
    }
    setSaving(false);
  }

  const onChangeContact = (id: string, field: keyof Contact, value: string) => {
    const index = contacts.findIndex(contact => contact._id == id);
    if (index != -1) {
      contacts[index][field] = value;
    }
    setContacts([...contacts]);
  }

  const onDeleteContact = (id: string) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter(contact => contact._id != id));
    }
  }

  const renderContacts = (contacts: Contact[]) => {
    return (
      <div className={styles.contacts}>
        <span>{t('contacts.intro', 'Lägg till minst en kontaktperson som ska ta emot anbudsförfrågningar. De kontaktpersoner ni lägger till här kommer synas som förslag om de saknas i beställarens leverantörsregister och visas som “verifierade av leverantören” om de finns med.')}</span>
        {contacts.map(contact => (
          <div className={styles.contact} key={contact._id}>
            <div className={[styles.column, styles.grow].join(" ")}>
              <div className={styles.row}>
                <div className={[styles.column, styles.grow].join(" ")}>
                  <Field
                    value={contact.name}
                    onChange={value => onChangeContact(contact._id, "name", value)}
                    placeholder={t('contacts.placeholder.name', 'Namn *')}
                    required
                    errorMessage={errorMessages?.[contact._id]?.name}
                  />
                </div>
                <div className={[styles.column, styles.grow].join(" ")}>
                  <Field
                    value={contact.email}
                    onChange={value => onChangeContact(contact._id, "email", value)}
                    placeholder={t('contacts.placeholder.email', 'E-post *')}
                    required
                    errorMessage={errorMessages?.[contact._id]?.email}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={[styles.column, styles.grow].join(" ")}>
                  <Field
                    value={contact.title}
                    onChange={value => onChangeContact(contact._id, "title", value)}
                    style={{fontWeight:400}}
                    placeholder={t('contacts.placeholder.title', 'Titel')}
                  />
                </div>
                <div className={[styles.column, styles.grow].join(" ")}>
                  <Field
                    value={contact.phone}
                    onChange={value => onChangeContact(contact._id, "phone", value)}
                    style={{fontWeight:400}}
                    placeholder={t('contacts.placeholder.phone', 'Telefon')}
                    errorMessage={errorMessages?.[contact._id]?.phone}
                  />
                </div>
              </div>
            </div>
            <div className={styles.column}>
              <BtButton color="white" size="xxs" disabled={contacts.length <= 1} onClick={() => onDeleteContact(contact._id)}>
                <span style={{color: "var(--gray-500)"}}><RemoveUserIcon /></span>
              </BtButton>
            </div>
          </div>
        ))}
        <div key="newContactButton">
          <BtButton color="white" size="xxs" leftIcon={<AddUserIcon/>} onClick={() => {
            setContacts([...contacts, {_id: newId(), name:"", email: "", title:"", phone:""}])
          }}>
            {t('contacts.button.add', 'Lägg till kontaktperson')}
          </BtButton>
        </div>
      </div>
    );
  }

  const renderOtherEmailAddresses = (otherEmailAddresses : OtherEmailAdress[]) => {
    const onRedirect = (email: string, comment: string, redirectToId: string) => {
      setRedirectEmail(email);
      setRedirectMessage(comment);
      const contact = contacts.find(contact => contact._id == redirectToId);
      if (contact) {
        setRedirectValue({label: `${contact.name} (${contact.email})`, value: contact._id});
      }
      setShowRedirect(true);
    }
    const onMessage = (email: string, comment: string) => {
      setMessageEmail(email);
      setMessageValue(comment);
      setShowMessage(true);
    }
    const onAddAsContact = (email: string) => {
      setContacts([...contacts, { _id: newId(), name: "", email, phone: "", title: "" }]);
    }
    const onSaveOtherEmail = (email: string, comment: string, redirectToId?: string) => {
      const otherEmail = otherEmailAddresses.find(o => o.email == email);
      if (otherEmail) {
        otherEmail.comment = comment;
        otherEmail.redirectToId = redirectToId;
        setOtherEmailAddresses([...otherEmailAddresses]);
      }
      setShowMessage(false);
      setShowRedirect(false);
    }

    const filteredOtherEmailAddresses = otherEmailAddresses
      .filter(address => !contacts.find(contact => contact.email.trim().toLowerCase() == address.email.trim().toLowerCase()));

    return (
      <div className={styles.otherEmails}>
        <span>{t('otherEmailAddresses.intro', 'Här visas vilka andra e-postadresser våra användare har registrerat för ditt företag (dessa syns endast för de beställare som lagt till dem). Kontrollera att dessa uppgifter är korrekta och aktuella, annars kommentera eller hänvisa till någon annan kontaktperson. Om uppgifterna stämmer och du vill göra dem tillgängliga för samtliga beställare i Accurator kan du lägga till en kontaktperson med den e-postadressen. Om inget visas så betyder det antingen att våra användare har samma kontaktpersoner som du angett, eller så är ditt organisationsnummer inte registrerat av våra användare i deras leverantörsregister.')}</span>

        {filteredOtherEmailAddresses.map(address => (
          <div className={styles.otherEmail} key={address.email}>
            <div className={styles.column}>
              <div className={styles.headline}>{address.email}</div>
              <TooltipContainer renderReferenceComponent={(className, ref) => <div className={`${styles.info} ${className}`} ref={ref}>{t('otherEmailAddresses.occurencesInSupplierDatabase', 'Används i {{count}} leverantörsregister.', { count: address.count })}</div>}>
                {address?.orgs?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {address?.orgs?.map(org => <span key={org}>{org}</span>)}
                  </div>
                )}
              </TooltipContainer>
            </div>
            <div className={styles.buttons}>
              <TooltipContainer renderReferenceComponent={(className, ref) => (
                <div className={className} ref={ref}>
                  <BtButton color="white" size="xxs" onClick={() => onMessage(address.email, address.comment ?? "")}>
                    <span style={{color: (address.comment && !address.redirectToId) ? "var(--cyan-500)" : "var(--gray-500)"}}><CommentIcon /></span>
                  </BtButton>
                </div>
              )}>
                {t('otherEmailAddresses.tooltip.comment', 'Kommentera')}
              </TooltipContainer>
              <TooltipContainer renderReferenceComponent={(className, ref) => (
                <div className={className} ref={ref}>
                  <BtButton color="white" size="xxs" onClick={() => onRedirect(address.email, address.comment ?? "", address.redirectToId ?? "")}>
                    <span style={{color: address.redirectToId ? "var(--cyan-500)" : "var(--gray-500)"}}><ForwardIcon /></span>
                  </BtButton>
                </div>
              )}>
                {t('otherEmailAddresses.tooltip.redirect', 'Hänvisa till annan kontaktperson')}
              </TooltipContainer>
              <TooltipContainer renderReferenceComponent={(className, ref) => (
                <div className={className} ref={ref}>
                  <BtButton color="white" size="xxs" onClick={() => onAddAsContact(address.email)} disabled={!!contacts.find(contact => contact.email.trim().toLowerCase() == address.email.trim().toLowerCase())}>
                    <span style={{color: "var(--gray-500)"}}><AddUserIcon /></span>
                  </BtButton>
                </div>
              )}>
                {t('otherEmailAddresses.tooltip.addContact', 'Lägg till som kontaktperson')}
              </TooltipContainer>
            </div>
          </div>
        ))}

        <Modal show={showRedirect} setShow={setShowRedirect} title={t('otherEmailAddresses.redirect.title', 'Hänvisning')} buttonInfo={{label: t('otherEmailAddresses.redirect.button.save', 'Spara'), action: () => onSaveOtherEmail(redirectEmail, redirectMessage, redirectValue?.value)}}>
          <div className={styles.modal}>
            <p>{t('otherEmailAddresses.redirect.intro', 'Hänvisa de som använder e-postadressen {{redirectEmail}} till en annan kontaktperson. Visas tillsammans med kontaktpersonen i inköparens leverantörsregister.', { redirectEmail })}</p>
            <Field label={t('otherEmailAddresses.redirect.label.message', 'Meddelande')} value={redirectMessage} onChange={setRedirectMessage}></Field>
            <Selector options={contacts.map(contact => ({ label: `${contact.name} (${contact.email})`, value: contact._id }))} label={t('otherEmailAddresses.redirect.label.contact', 'Kontaktperson')} value={redirectValue} onChange={setRedirectValue}></Selector>
          </div>
        </Modal>

        <Modal show={showMessage} setShow={setShowMessage} title={t('otherEmailAddresses.comment.title', 'Meddelande')} buttonInfo={{label: t('otherEmailAddresses.comment.button.save', 'Spara'), action: () => onSaveOtherEmail(messageEmail, messageValue)}}>
          <div className={styles.modal}>
            <p>{t('otherEmailAddresses.comment.intro', 'Skriv ett meddelande till de som använder {{messageEmail}}. Visas tillsammans med kontaktpersonen i inköparens leverantörsregister.', { messageEmail })}</p>
            <Field label={t('otherEmailAddresses.comment.label.message', 'Meddelande')} value={messageValue} onChange={setMessageValue}></Field>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <AppLayout title={t('pageTitle', 'Leverantörspresentation')}>
      <div style={{margin: "32px"}}>
        <Card gap={24} className={styles.card}>
          {loading ? (
            <div style={{minHeight: "500px", display: "flex", justifyContent: "center", alignItems: "center"}}>
              <Loading type="inline"></Loading>
            </div>
          ) : (
            <>
              <span>{t('intro', 'Ta kontroll över hur ert företag presenteras i inköparnas leverantörsdatabas. Uppgifterna nedan kombineras med inköparnas egna uppgifter och visas då de väljer leverantörer till sina inköp.')}</span>
              <div style={{display:"flex", gap: "32px"}}>
                <Field label={t('label.name', 'Företagsnamn')} value={name} onChange={setName}/>
                <Field disabled label={t('label.orgNo', 'Organisationsnummer')} value={orgNr} onChange={setOrgNr} style={{backgroundColor: "var(--gray-50)", borderRadius: "6px"}}/>
              </div>

              <LogoForm
                logo={logo}
                scope={FileScope.SupplierProfileLogo}
                scopeOptions={{  }}
                image={newLogo}
                onChange={file => {
                  if (file) {
                    setNewLogo(file);
                    setLogo(undefined);
                  } else {
                    setNewLogo(undefined);
                    setLogo(undefined);
                  }
                }}
              />

              <Field label={t('label.presentation', 'Presentation')} multiline value={presentation} onChange={setPresentation} />

              <Field label={t('label.address', 'Adress')} multiline value={address} onChange={setAddress} />

              <Field label={t('label.website', 'Webbplats')} value={website} onChange={setWebsite} />

              <Selector<SelectorType, true> isMulti isSearchable required options={areaOptions} label={t('label.areas', 'Yrkeskategorier')} value={areas} onChange={(values: MultiValue<SelectorType>) => setAreas([...values])} />

              <PlaceSelector places={places} onChange={setPlaces} required />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontWeight: 500, marginBottom: '8px', fontSize: '14px' }}>{t('liabilityInsurance.headline', 'Ansvarsförsäkring')}</div>
                  {existingLiabilityInsurances.length === 0 && newLiabilityInsurances.length === 0 && (
                    <span style={{ color: 'var(--gray-700)', fontWeight: 500 }}>{t('liabilityInsurance.empty', 'Ladda upp befintlig försäkring')}</span>
                  )}

                  {existingLiabilityInsurances.map((insurance, index) => {
                    const filePath = insurance.file?.file;
                    const fileName = insurance.file?.fileName;

                    return (
                      <FileUploadRow
                        key={insurance._id || index}
                        existingFile={{
                          file: filePath,
                          fileName: fileName,
                          comment: insurance.comment || '',
                          validUntilDate: insurance.validUntilDate,
                          replacementFile: insurance.replacementFile,
                        }}
                        setExistingFile={(updated) => {
                          if (updated === null) {
                            // Remove the item
                            setExistingLiabilityInsurances(prev => prev.filter((_, i) => i !== index));
                          } else {
                            const updatedInsurances = [...existingLiabilityInsurances];
                            updatedInsurances[index] = {
                              ...updatedInsurances[index],
                              comment: updated.comment,
                              validUntilDate: updated.validUntilDate,
                            };
                            if (updated.file === undefined && updated.fileName === undefined) {
                              updatedInsurances[index].file = undefined;
                            }
                            updatedInsurances[index].replacementFile = updated.replacementFile;
                            setExistingLiabilityInsurances(updatedInsurances);
                          }
                        }}
                        newFile={null}
                        setNewFile={() => { }}
                        label={t('liabilityInsurance.headline', 'Ansvarsförsäkring')}
                        hasRemoveButton={true}
                        hasRemoveText={true}
                        allowMetadataOnly={true}
                        isDatePickerFocused={focusedDatePicker !== null && focusedDatePicker.sectionKey === 'liability' && focusedDatePicker.index === index && focusedDatePicker.isNew === false}
                        renderFields={(file) => [
                          <Field
                            key="comment"
                            label={t('liabilityInsurance.comment', 'Kommentar')}
                            value={file.comment || ''}
                            onChange={(value) => {
                              const updated = [...existingLiabilityInsurances];
                              updated[index] = { ...updated[index], comment: value };
                              setExistingLiabilityInsurances(updated);
                            }}
                          />,
                          <DatePicker
                            key="validUntilDate"
                            label={t('liabilityInsurance.validUntil', 'Giltigt till')}
                              selected={file.validUntilDate ? new Date(file.validUntilDate) : undefined}
                              onChange={(date) => {
                                const updated = [...existingLiabilityInsurances];
                                updated[index] = { ...updated[index], validUntilDate: date || undefined };
                                setExistingLiabilityInsurances(updated);
                              }}
                              onFocus={() => setFocusedDatePicker({ sectionKey: 'liability', index, isNew: false })}
                              onBlur={() => setFocusedDatePicker(null)}
                              isClearable
                            />
                          ]}
                        />
                    );
                  })}

                  {newLiabilityInsurances.map((newFile, index) => (
                    <FileUploadRow
                      key={newFile.key}
                      existingFile={null}
                      setExistingFile={() => { }}
                      newFile={newFile}
                      setNewFile={(updated) => {
                        if (updated === null) {
                          setNewLiabilityInsurances(prev => prev.filter((_, i) => i !== index));
                        } else {
                          const updatedFiles = [...newLiabilityInsurances];
                          updatedFiles[index] = { ...updatedFiles[index], file: updated.file };
                          setNewLiabilityInsurances(updatedFiles);
                        }
                      }}
                      label={t('liabilityInsurance.headline', 'Ansvarsförsäkring')}
                      hasRemoveButton={true}
                      allowMetadataOnly={true}
                      isDatePickerFocused={focusedDatePicker !== null && focusedDatePicker.sectionKey === 'liability' && focusedDatePicker.index === index && focusedDatePicker.isNew === true}
                      renderFields={(file) => [
                        <Field
                          key="comment"
                          label={t('liabilityInsurance.comment', 'Kommentar')}
                          value={(file as any).comment || ''}
                          onChange={(value) => {
                            const updated = [...newLiabilityInsurances];
                            updated[index] = { ...updated[index], comment: value } as any;
                            setNewLiabilityInsurances(updated);
                          }}
                        />,
                        <DatePicker
                          key="validUntilDate"
                          label={t('liabilityInsurance.validUntil', 'Giltigt till')}
                          selected={(file as any).validUntilDate ? new Date((file as any).validUntilDate) : undefined}
                          onChange={(date) => {
                            const updated = [...newLiabilityInsurances];
                            updated[index] = { ...updated[index], validUntilDate: date || undefined } as any;
                            setNewLiabilityInsurances(updated);
                          }}
                          onFocus={() => setFocusedDatePicker({ sectionKey: 'liability', index, isNew: true })}
                          onBlur={() => setFocusedDatePicker(null)}
                          isClearable
                        />
                      ]}
                    />
                  ))}

                  <BtButton
                    color="white"
                    size="xxs"
                    style={{ width: 'fit-content', marginTop: '8px' }}
                    onClick={() => {
                      setNewLiabilityInsurances(prev => [...prev, { key: `new-${Date.now()}`, file: undefined }]);
                    }}
                  >
                    {t('liabilityInsurance.headline', 'Ansvarsförsäkring')}
                  </BtButton>
                <div className={styles.insuranceContainer}>
                  <div className={styles.logoContainer}>
                    <span style={{ fontWeight: 500 }}>{t('insurancePromotion.title', 'Är din försäkring aktuell?')}</span>
                    <div className={styles.logos}>
                      <AccuratorLogo width="120" height="42" />
                      <SoderbergpartnersLogo />
                    </div>
                  </div>
                  <span style={{ fontWeight: 500 }}>{t('insurancePromotion.subtitle', 'Entreprenadförsäkringar i samarbete med Söderberg & Partners')}</span>
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)', width: 'clamp(200px, 60%, 800px)', marginBottom: '8px' }}>
                    {t('insurancePromotion.intro', 'Genom vårt samarbete med Söderberg & Partners kan vi nu erbjuda våra kunder exklusiva entreprenadförsäkringar till konkurrenskraftiga priser. Dessa försäkringar är skräddarsydda för byggbranschen och ger dig det skydd du behöver för att trygga dina projekt. Spara 10% på din entreprenadförsäkring idag!')}
                  </span>
                  <BtButton
                    size="xxs"
                    color="white"
                    leftIcon={<ShoppingCartIcon />}
                    style={{ width: 'fit-content' }}
                    onClick={() => {
                      logInsuranceClick();
                      window.open('https://www.soderbergpartners.se/forsakringar/offert-formular/', '_blank');
                    }}
                  >
                    {t('insurancePromotion.button', 'Teckna ny ansvarsförsäkring')}
                  </BtButton>
                </div>
              </div>

              <div className={styles.row} style={{borderTop: "1px solid var(--gray-300)", paddingTop: "24px"}}>
                <div className={[styles.column, styles.label].join(" ")}>
                  {t('files.title', 'Övriga filer')}
                </div>
                <div className={[styles.column, styles.grow].join(" ")}>
                  <FileUpload
                      existingFiles={existingFiles as any}
                      setExistingFiles={setExistingFiles as any}
                      newFiles={newFiles as any}
                      setNewFiles={setNewFiles as any}
                    label={t('files.button.add', 'Lägg till filer')}
                    hasRemoveAllButton={true}
                    scope={FileScope.SupplierProfileFile}
                    scopeOptions={{ organizationNumber: orgNr }}
                  />
                </div>
              </div>

              <div className={styles.row} style={{borderTop: "1px solid var(--gray-300)", paddingTop: "24px"}}>
                <div className={[styles.column, styles.label].join(" ")}>
                  <span>{t('contacts.title', 'Kontaktpersoner')} <span style={{ color: 'var(--orange-500)' }}>*</span></span>
                </div>
                <div className={[styles.column, styles.grow].join(" ")}>
                  {renderContacts(contacts)}
                </div>
              </div>

              <div className={styles.row} style={{borderTop: "1px solid var(--gray-300)", paddingTop: "24px"}}>
                <div className={[styles.column, styles.label].join(" ")}>
                  {t('otherEmailAddresses.title', 'Andra e-postadresser')}
                </div>
                <div className={[styles.column, styles.grow].join(" ")}>
                  {renderOtherEmailAddresses(otherEmailAddresses)}
                </div>
              </div>

              <div style={{borderTop: "1px solid var(--gray-300)", paddingTop: "24px", color: "var(--gray-700)", fontWeight: 500, fontSize: "14px" }}>
                <TooltipContainer renderReferenceComponent={(className, ref) => (
                  <div
                    style={{ width: 'fit-content' }}
                    ref={ref}
                    className={className}
                  >
                    <BtButton onClick={onSave} loaderShow={saving} disabled={(!areas?.length || !places?.length || !contacts?.length)}>{t('button.save.text', 'Spara')}</BtButton>
                  </div>
                )}>
                  {
                    (!areas?.length || !places?.length || !contacts?.length) &&
                      t('button.save.tooltip.emptyFields', '{{emptyFields_list}} är obligatoriska', { emptyFields: [areas?.length ? '' : 'Yrkeskategorier', places?.length ? '' : 'Platser', contacts?.length ? '' : 'Kontaktpersoner'].filter(x => x) })
                  }
                </TooltipContainer>
              </div>
            </>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}

export default PageSupplierPresentation;
